import React, { ReactNode, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission, useAnyPermission, useAllPermissions } from '../../hooks/usePermission';
import { useRole, useAnyRole } from '../../hooks/usePermission';
import { useAuth } from '../../contexts/AuthContext';

interface RoleGuardProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  role?: string;
  roles?: string[];
  children: ReactNode;
  fallback?: ReactNode;
  redirectTo?: string;
}

export function RoleGuard({
  permission,
  permissions,
  requireAll = false,
  role,
  roles,
  children,
  fallback = null,
  redirectTo,
}: RoleGuardProps): ReactElement | null {
  const { isAuthenticated, isLoading } = useAuth();
  
  const hasSinglePermission = usePermission(permission ?? '');
  const hasAnyPermissions = useAnyPermission(permissions ?? []);
  const hasAllRequiredPermissions = useAllPermissions(permissions ?? []);
  const hasSingleRole = useRole(role ?? '');
  const hasAnyRequiredRoles = useAnyRole(roles ?? []);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback}</>;
  }

  let hasAccess = true;

  if (permission) {
    hasAccess = hasAccess && hasSinglePermission;
  }

  if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasAccess = hasAccess && hasAllRequiredPermissions;
    } else {
      hasAccess = hasAccess && hasAnyPermissions;
    }
  }

  if (role) {
    hasAccess = hasAccess && hasSingleRole;
  }

  if (roles && roles.length > 0) {
    hasAccess = hasAccess && hasAnyRequiredRoles;
  }

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface PermissionGuardProps {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function PermissionGuard({
  permission,
  children,
  fallback = null,
}: PermissionGuardProps): ReactElement {
  const hasPermission = usePermission(permission);

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface ShowIfPermissionProps {
  permission: string;
  children: ReactNode;
}

export function ShowIfPermission({ permission, children }: ShowIfPermissionProps): ReactElement | null {
  const hasPermission = usePermission(permission);
  return hasPermission ? <>{children}</> : null;
}

interface HideIfPermissionProps {
  permission: string;
  children: ReactNode;
}

export function HideIfPermission({ permission, children }: HideIfPermissionProps): ReactElement | null {
  const hasPermission = usePermission(permission);
  return hasPermission ? null : <>{children}</>;
}

export default RoleGuard;
